import { SideNavV2, SideNavV2Section } from "@/components/interactive/SideNavV2";
import { ProjectNavbarAdminModeControl } from "@/components/navigation/ProjectNavbarAdminModeControl";
import { getNavigationV2AppReturnPath } from "@/components/navigation/navigation-v2-return-path";
import { useTeamSideNavItems } from "@/components/navigation/use-team-side-nav-items";
import { AmplitudeEvent, useAmplitudeOnClick } from "@/utils/amplitude";
import { classNames } from "@/utils/classnames";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { Routes } from "@/utils/routes";
import { useTheme } from "next-themes";
import React, { useMemo } from "react";

export interface TeamNavbarV3Props {
  activePage?: string;
  activeSubPage?: string;
}

const getGlobalBackToAppHref = ({
  environmentId,
  projectId,
  team,
}: {
  environmentId: string | undefined;
  projectId: string | undefined;
  team: ReturnType<typeof useRouteAndTeamInfo>["team"];
}) => {
  if (projectId != null && environmentId != null) {
    return Routes.project.environment.root(projectId, environmentId);
  }

  const storedReturnPath = getNavigationV2AppReturnPath();
  if (storedReturnPath != null) {
    return storedReturnPath;
  }

  const firstProjectWithEnvironment = team?.projects.find((project) => project.environments.length);
  const firstEnvironment = firstProjectWithEnvironment?.environments[0];
  if (firstProjectWithEnvironment != null && firstEnvironment != null) {
    return Routes.project.environment.root(firstProjectWithEnvironment.id, firstEnvironment.id);
  }

  return Routes.projects;
};

export const TeamNavbarV3: React.FC<TeamNavbarV3Props> = ({ activePage }) => {
  const { resolvedTheme } = useTheme();
  const { environmentId, projectId, team } = useRouteAndTeamInfo();
  const { inlineItems, adminItems } = useTeamSideNavItems("TeamNavbarV3");
  const trackNavbarClick = useAmplitudeOnClick({
    event: AmplitudeEvent.TEAM_NAVBAR_CLICK,
    component: "TeamNavbarV3",
    href: "",
    text: "",
    description: "",
  });
  const backHref = useMemo(
    () => getGlobalBackToAppHref({ environmentId, projectId, team }),
    [environmentId, projectId, team],
  );
  const themeMode =
    resolvedTheme === "dark" ? "dark" : resolvedTheme === "light" ? "light" : undefined;

  const sections = useMemo<SideNavV2Section<string>[]>(() => {
    // Deliberately unlabelled: the ordinary destinations are the default content of this
    // navbar and do not need a header to distinguish them from anything.
    const sections: SideNavV2Section<string>[] = [{ id: "global-settings", items: inlineItems }];

    // An empty list means there is no section to render rather than an empty one to hide.
    if (adminItems.length > 0) {
      sections.push({
        id: "admin-navigation",
        label: "Admin",
        items: [
          {
            id: "team-admin-links",
            label: "Admin links",
            icon: "lock",
            admin: true,
            // No href: there is no team-level admin landing page, so this row exists purely
            // to host the menu. SideNavV2 renders an hrefless row as a button, and
            // childrenDisplay "menu" keeps the children in the hover/focus popup rather than
            // expanding them inline. The children keep their own page ids, so whichever one
            // you are on still marks this row active.
            childrenDisplay: "menu",
            children: adminItems,
          },
        ],
      });
    }

    return sections;
  }, [adminItems, inlineItems]);

  return (
    <div
      className={classNames(
        "sticky top-0 flex h-screen",
        "bg-cds-surface-active dark:bg-cds-inset-dark",
      )}
    >
      <SideNavV2
        workspaceLabel={team?.name ?? "Global settings"}
        headerVariant={"back"}
        backLabel={"Back to app"}
        backHref={backHref}
        sections={sections}
        activeItemId={activePage}
        onBackToApp={() => {
          trackNavbarClick({
            href: backHref,
            text: "Back to app",
            mainPageId: "back-to-app",
            projectId,
            environmentId,
          });
        }}
        showToggleButton={false}
        // The Admin menu is the only nested item in this navbar, and its rows are plain page
        // links. A detail pane for them would only restate the label plus a generic "Open X to
        // view and manage this page", so it reads as a stray second popup rather than
        // information. This gates just that pane; the Admin flyout itself is unaffected.
        showDetailPanes={false}
        scrollPositionKey={"team-nav-v3"}
        themeMode={themeMode}
        className={"h-full !rounded-none bg-cds-surface-active dark:bg-cds-inset-dark"}
        footerAccessory={<ProjectNavbarAdminModeControl />}
        aria-label={"Global settings navigation"}
      />
    </div>
  );
};
